import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import {
  getMediaOptions,
  type ImagesType,
  type ProjectImageType,
  useMedia,
  useProjectMedia,
} from '@/api/media';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import { useEstate } from '@/features/estates-property/api/estate';

import { useDeleteImage } from '../api/delete-image';
import { useUploadImage } from '../api/upload-image';
import { ACCEPTED_IMAGE_INPUT_TYPE, uploadImagesSchema } from '../schema/upload-media';
import { MediaData } from './media-data';
import type { UploadMediaModalType } from './upload-media-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/media/images'
);

export const MediaImagesDashboard = () => {
  const { estateId } = route.useParams();

  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { data } = useMedia({ id: estateId });
  const { data: estate } = useEstate({ id: estateId });
  const { data: projectMedia } = useProjectMedia({ id: estate?.projectId });

  const images = useMemo(
    () => [...Object.values(data.images).flat(), ...Object.values(projectMedia.images).flat()],
    [data, projectMedia]
  );

  const dataImages = useMemo(() => {
    const dates = new Set<string>([
      ...Object.keys(data.images),
      ...Object.keys(projectMedia.images),
    ]);
    const groupedImages = Array.from(dates).reduce<
      Record<string, ImagesType[string] | ProjectImageType[]>
    >((acc, date) => {
      const estateImages = data.images[date] || [];
      const projectImages = projectMedia.images[date] || [];
      acc[date] = [...estateImages, ...projectImages];
      return acc;
    }, {});
    return groupedImages;
  }, [data, projectMedia]);

  const { mutate: deleteImage } = useDeleteImage({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
        toast.success(t('successfullyDeletedImage', { autoClose: 100 }));
      },
    },
  });

  const { mutate: uploadImage } = useUploadImage({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey }),
    },
  });

  const handleDelete = (ids: string[]) => {
    openModal('confirmation-modal', {
      title: `${t('delete')} ${t('image')} `,
      description: t('confirmDeleteDocument'),
      confirmationText: t('delete'),
      onSubmit: async () => await deleteImage({ estateId, imageIds: ids }),
    });
  };

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadImage({ images: dataTransfer.files, estateId });
    closeModal();
  };

  const handleOpenModal = () => {
    openModal('upload-media', {
      submitHandler,
      acceptedType: ACCEPTED_IMAGE_INPUT_TYPE,
      acceptedMediaSchema: uploadImagesSchema(),
    });
  };

  const openThumbnailModal = async () => {
    openModal('select-thumbnail', { id: estateId, images });
  };

  return (
    <MediaData
      data={dataImages}
      onUpload={handleOpenModal}
      handleDelete={handleDelete}
      onChooseThumbnail={openThumbnailModal}
      isThumbnailDisabled={!images.length}
      page="images"
      type="estate"
      estateId={estateId}
    />
  );
};
